import React, { useEffect, useRef } from "react";

/**
 * Inner-page hero with parallax background image, cream overlay,
 * eyebrow + large headline + supporting copy.
 */
export default function PageHero({ eyebrow, title, italicWord, description, image }) {
  const bgRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const sec = sectionRef.current;
      const bg = bgRef.current;
      if (!sec || !bg) return;
      const rect = sec.getBoundingClientRect();
      const offset = -rect.top * 0.35;
      bg.style.transform = `translate3d(0, ${offset}px, 0) scale(1.1)`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Split title around italic word
  let before = title, after = "";
  if (italicWord && title.includes(italicWord)) {
    const idx = title.indexOf(italicWord);
    before = title.slice(0, idx);
    after = title.slice(idx + italicWord.length);
  }

  return (
    <section ref={sectionRef} className="relative h-[62vh] min-h-[460px] overflow-hidden bg-[#1F1B17] mt-20">
      <div ref={bgRef} className="absolute inset-0 will-change-transform">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1F1B17]/45 via-[#1F1B17]/40 to-[#1F1B17]/85"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F1B17]/55 via-transparent to-transparent"></div>
      </div>
      <div className="relative z-10 h-full flex items-end pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-8 text-[#F5EFE6]">
            <div className="text-[11px] tracking-[0.5em] uppercase text-[#D4A857] mb-5">{eyebrow}</div>
            <h1 className="font-display headline-tight text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-shadow-dark">
              {before}{italicWord && (<span className="italic text-[#D4A857]">{italicWord}</span>)}{after}
            </h1>
          </div>
          {description && (
            <p className="lg:col-span-4 text-[#E8DCC4] leading-relaxed self-end text-sm md:text-base">{description}</p>
          )}
        </div>
      </div>
      {/* Scroll cue */}
      <div className="absolute bottom-5 left-6 lg:left-12 z-10 flex items-center gap-3">
        <div className="text-[10px] tracking-[0.5em] uppercase text-[#D4A857]">Scroll to Explore</div>
        <div className="h-px w-10 bg-[#D4A857]"></div>
      </div>
    </section>
  );
}
